import { useMemo } from 'react'

import type { ActivationGroup } from '@/features/activationGroup/types'
import { ResourceCategory } from '@/features/activationGroup/types'
import { ALL_RESULTS } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useBidHistoriesPageQuery } from '@/features/bidding/hooks/useBidHistoriesPageQuery'
import { useBidOfferDataRequirements } from '@/features/bidding/hooks/useBidOfferDataRequirements'
import { useBidOfferDerivedData } from '@/features/bidding/hooks/useBidOfferDerivedData'
import { useBidOfferEligibleActivationGroups } from '@/features/bidding/hooks/useBidOfferEligibleActivationGroups'
import { useBidOfferGroupParams } from '@/features/bidding/hooks/useBidOfferGroupParams'
import { useGroupedBidsPageQuery } from '@/features/bidding/hooks/useGroupedBidsPageQuery'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidHistory } from '@/features/bidding/types/bidHistory'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { findExpiredPtuPrefillSource } from '@/features/bidding/utils/ptuPrefillSources'

export type BidOfferConfirmPriceData = {
  groupParams: GroupedBidParams
  bidHistories: BidHistory[]
  fixedActivationGroupBids: ExtendedBid[]
  eligibleActivationGroups: ActivationGroup[]
}

type UseBidOfferConfirmPriceDataReturn =
  | { isLoading: true; data: null }
  | { isLoading: false; data: BidOfferConfirmPriceData }

export function useBidOfferConfirmPriceData(): UseBidOfferConfirmPriceDataReturn {
  // 1. Get selected country from context
  const { selectedCountry } = useBiddingContext()

  // 2. Get group params from URL (and redirect if missing)
  const groupParamsResult = useBidOfferGroupParams()

  // Early return if groupParams is still loading
  if (groupParamsResult.isLoading) {
    return { isLoading: true, data: null }
  }

  const groupParams = groupParamsResult.data

  // Call helper hook with guaranteed non-null groupParams
  return useBidOfferConfirmPriceDataLoaded(groupParams, selectedCountry)
}

/**
 * Helper hook that handles all data fetching when groupParams is guaranteed to be loaded.
 * This separation allows us to work with non-null groupParams throughout.
 */
function useBidOfferConfirmPriceDataLoaded(
  groupParams: GroupedBidParams,
  selectedCountry: CountryIdentifier,
): UseBidOfferConfirmPriceDataReturn {
  const { currentTime, isPtuEditable } = usePtuExpiration()

  // 2. Determine what data is required for this bid scenario
  const requirements = useBidOfferDataRequirements(groupParams)

  // 3. Fetch grouped bids page
  const { groupedBidsPage, isLoading: isLoadingGroupedBids } = useGroupedBidsPageQuery(
    {
      pagination: { pageSize: ALL_RESULTS, page: 0 },
      filter: {
        items: [
          { field: 'portfolio', operator: 'equals', value: groupParams.portfolioCode },
          { field: 'marketProgram', operator: 'equals', value: groupParams.marketProgram },
          { field: 'deliveryDay', operator: 'equals', value: groupParams.deliveryDay },
          { field: 'bidType', operator: 'equals', value: groupParams.bidType },
        ],
      },
      country: selectedCountry,
    },
    { enabled: true },
  )

  // 4. Fetch eligible activation groups
  const { eligibleActivationGroups: rawEligibleActivationGroups, isLoading: isLoadingActivationGroups } =
    useBidOfferEligibleActivationGroups(groupParams)

  // 4a. Filter activation groups to only C_AND_I resource category if required
  const eligibleActivationGroups = requirements.requiresOnlyCAndIActivationGroups
    ? rawEligibleActivationGroups?.filter((ag) => ag.resourceCategory === ResourceCategory.C_AND_I)
    : rawEligibleActivationGroups

  // 5. Fetch bid histories page
  const { bidHistoriesPage, isFetching: isFetchingBidHistories } = useBidHistoriesPageQuery(
    {
      pagination: { pageSize: ALL_RESULTS, page: 0 },
      filter: {
        items: [
          { field: 'portfolio', operator: 'equals', value: groupParams.portfolioCode },
          { field: 'marketProgram', operator: 'equals', value: groupParams.marketProgram },
          { field: 'deliveryDay', operator: 'equals', value: groupParams.deliveryDay },
          { field: 'bidType', operator: 'equals', value: groupParams.bidType },
        ],
      },
      countryFilter: selectedCountry,
      sorting: [],
    },
    { enabled: true },
  )

  // 6. Extract latest non-draft and accepted bids per activation group
  const { latestNonDraftBidsByActivationGroup, latestAcceptedBidsByActivationGroup } = useBidOfferDerivedData({
    bidHistories: bidHistoriesPage?.bidHistories,
  })

  const activationGroupBids = groupedBidsPage?.groupedBids.flatMap((group) => Object.values(group.bids))

  // 7. Build a corrected version of all bids where expired PTUs are automatically fixed
  const fixedActivationGroupBids = useMemo(() => {
    if (isFetchingBidHistories) return activationGroupBids

    return activationGroupBids?.map((bid) => {
      const acceptedBidForGroup = latestAcceptedBidsByActivationGroup.get(bid.activationGroupUuid)
      const latestBidForGroup = latestNonDraftBidsByActivationGroup.get(bid.activationGroupUuid)

      const updatedPtus = bid.ptus.map((ptu) => {
        const editable = isPtuEditable(ptu.ptu.start, bid.bidType, bid.marketProgram)
        if (editable) return ptu

        // An expired PTU is submitted with the accepted version's values, falling back to the latest
        // version that was sent. This is the last screen before sending, so reading the latest offer
        // here would put a rejected volume back on a past PTU however the earlier steps filled it
        // (BID-209).
        const matchingPtu = findExpiredPtuPrefillSource(ptu.ptu.start, acceptedBidForGroup, latestBidForGroup)

        if (!matchingPtu) {
          // Nothing was ever accepted or sent for this PTU ➜ set to 0 MW, keep the existing price
          // columns but zero their quantities
          const zeroChunks = (ptu.ptuChunks ?? []).map((chunk) => ({
            ...chunk,
            offeredVolume: { ...chunk.offeredVolume, quantity: 0 },
          }))
          return {
            ...ptu,
            offeredVolume: { ...ptu.offeredVolume, quantity: 0 },
            ptuChunks: zeroChunks,
          }
        }

        // Copy volumes & chunks from that same version. The submitted quantity must equal the
        // source PTU's offeredVolume - that is the field Portfolio Manager diffs against its
        // baseline, at both PTU and chunk level. If new price columns were added in the current
        // (draft) bid, they will not be present in it. We merge these chunks and set their
        // quantities to **0** so that the disabled PTUs display "0" instead of "-" in the UI.
        const latestChunks = matchingPtu.ptuChunks ?? []
        const currentChunks = ptu.ptuChunks ?? []

        // Add any new chunks that are missing in the latest bid, with quantity 0
        const mergedChunks = [
          ...latestChunks.map((latestChunk) => ({ ...latestChunk })),
          ...currentChunks
            .filter(
              (currentChunk) =>
                !latestChunks.some((latestChunk) => latestChunk.offeredPrice === currentChunk.offeredPrice),
            )
            .map((currentChunk) => ({
              ...currentChunk,
              offeredVolume: { ...currentChunk.offeredVolume, quantity: 0 },
            })),
        ]

        return {
          ...ptu,
          offeredVolume: { ...ptu.offeredVolume, quantity: matchingPtu.offeredVolume.quantity },
          ptuChunks: mergedChunks,
        }
      })

      return { ...bid, ptus: updatedPtus }
    })
  }, [
    activationGroupBids,
    latestNonDraftBidsByActivationGroup,
    latestAcceptedBidsByActivationGroup,
    currentTime.toMillis(),
  ])

  // 8. Filter fixed bids to only include eligible activation groups
  const eligibleActivationGroupUuids = useMemo(
    () => new Set(eligibleActivationGroups?.map((ag) => ag.uuid) ?? []),
    [eligibleActivationGroups],
  )

  const filteredFixedActivationGroupBids = useMemo(
    () => fixedActivationGroupBids?.filter((bid) => eligibleActivationGroupUuids.has(bid.activationGroupUuid)),
    [fixedActivationGroupBids, eligibleActivationGroupUuids],
  )

  // Check if still loading
  const isLoading = isLoadingGroupedBids || isLoadingActivationGroups || isFetchingBidHistories

  if (isLoading || !groupedBidsPage || !filteredFixedActivationGroupBids || !eligibleActivationGroups) {
    return { isLoading: true, data: null }
  }

  return {
    isLoading: false,
    data: {
      groupParams,
      bidHistories: bidHistoriesPage?.bidHistories ?? [],
      fixedActivationGroupBids: filteredFixedActivationGroupBids,
      eligibleActivationGroups,
    },
  }
}
